/**
 * Inline-Edit-Logik für AccountPanel-Felder.
 * Erstellt Input-Rows und verwaltet Speichern/Abbrechen.
 */
import type { SupabaseClient } from '../network/SupabaseClient';
import type { Engine } from '../core/Engine';
import { fontSm, fontBase, padMd, gapMd, radius } from './UITheme';

const BRD = 'rgba(100,180,255,0.2)';
const ACCENT = '#8cf';
const MUTED = 'rgba(100,180,255,0.45)';
const ERR_CLR = '#f88';
const OK_CLR = '#8f8';

export interface FieldEditContext {
  supabase: SupabaseClient;
  engine: Engine;
  data: { email: string; displayName: string; accountName: string; playerId: string; avatarId: number };
  showStatus: (msg: string, color: string) => void;
}

export function startFieldEdit(
  ctx: FieldEditContext, sec: HTMLDivElement, row: HTMLDivElement,
  valEl: HTMLSpanElement, key: string, label: string,
): void {
  const old = valEl.textContent ?? '';
  const input = document.createElement('input');
  Object.assign(input.style, inputStyle());
  input.type = key === 'email' ? 'email' : 'text';
  input.value = old;
  input.placeholder = label;
  const restore = () => { editRow.remove(); row.style.display = 'flex'; };
  const btns = saveCancelBtns(
    async () => {
      const v = input.value.trim();
      if (!v || v === old) { restore(); return; }
      if (key === 'email') await saveEmail(ctx, v, valEl, restore);
      else if (key === 'displayName') await saveDisplayName(ctx, v, valEl, restore);
    },
    restore,
  );
  const editRow = document.createElement('div');
  Object.assign(editRow.style, { display: 'flex', flexDirection: 'column', gap: '6px' });
  editRow.append(input, btns);
  row.style.display = 'none';
  sec.appendChild(editRow);
  input.focus();
}

export function startPasswordEdit(
  ctx: FieldEditContext, sec: HTMLDivElement, row: HTMLDivElement,
): void {
  const pw1 = document.createElement('input');
  Object.assign(pw1.style, inputStyle());
  pw1.type = 'password'; pw1.placeholder = 'Neues Passwort';
  const pw2 = document.createElement('input');
  Object.assign(pw2.style, inputStyle());
  pw2.type = 'password'; pw2.placeholder = 'Passwort bestätigen';
  const restore = () => { editRow.remove(); row.style.display = 'flex'; };
  const btns = saveCancelBtns(
    async () => {
      if (!pw1.value || pw1.value.length < 6) { ctx.showStatus('Min. 6 Zeichen', ERR_CLR); return; }
      if (pw1.value !== pw2.value) { ctx.showStatus('Passwörter stimmen nicht überein', ERR_CLR); return; }
      const { error } = await ctx.supabase.auth.updateUser({ password: pw1.value });
      if (error) { ctx.showStatus(error.message, ERR_CLR); return; }
      ctx.showStatus('Passwort geändert', OK_CLR);
      restore();
    },
    restore,
  );
  const editRow = document.createElement('div');
  Object.assign(editRow.style, { display: 'flex', flexDirection: 'column', gap: '6px' });
  editRow.append(pw1, pw2, btns);
  row.style.display = 'none';
  sec.appendChild(editRow);
  pw1.focus();
}

async function saveEmail(
  ctx: FieldEditContext, email: string, valEl: HTMLSpanElement, restore: () => void,
): Promise<void> {
  const { error } = await ctx.supabase.auth.updateUser({ email });
  if (error) { ctx.showStatus(error.message, ERR_CLR); return; }
  ctx.data.email = email;
  valEl.textContent = email;
  ctx.showStatus('Bestätigungs-E-Mail gesendet', OK_CLR);
  restore();
}

async function saveDisplayName(
  ctx: FieldEditContext, name: string, valEl: HTMLSpanElement, restore: () => void,
): Promise<void> {
  const res = await ctx.supabase.functions.invoke('update-profile', { body: { display_name: name } });
  if (res.error) { ctx.showStatus('Fehler beim Speichern', ERR_CLR); return; }
  const body = res.data as Record<string, unknown>;
  if (body['error']) { ctx.showStatus(body['error'] as string, ERR_CLR); return; }
  ctx.data.displayName = name;
  valEl.textContent = name;
  ctx.showStatus('Name geändert', OK_CLR);
  ctx.engine.events.emit('account:name-changed', { name });
  restore();
}

function saveCancelBtns(onSave: () => void, onCancel: () => void): HTMLDivElement {
  const wrap = document.createElement('div');
  Object.assign(wrap.style, { display: 'flex', gap: gapMd() });
  for (const [label, color, fn] of [['Speichern', ACCENT, onSave], ['Abbrechen', MUTED, onCancel]] as const) {
    const b = document.createElement('button');
    Object.assign(b.style, {
      flex: '1', background: 'transparent', border: `1px solid ${color}`,
      borderRadius: radius(), color, fontFamily: 'monospace',
      fontSize: fontSm(), padding: padMd(), cursor: 'pointer',
    });
    b.textContent = label;
    b.addEventListener('click', fn);
    wrap.appendChild(b);
  }
  return wrap;
}

function inputStyle(): Partial<CSSStyleDeclaration> {
  return {
    background: 'rgba(0,0,0,0.4)', border: `1px solid ${BRD}`,
    borderRadius: radius(), color: '#cde', fontFamily: 'monospace',
    fontSize: fontBase(), padding: padMd(), outline: 'none', width: '100%',
    boxSizing: 'border-box',
  };
}

export function makeEditBtn(onClick: () => void): HTMLSpanElement {
  const btn = document.createElement('span');
  Object.assign(btn.style, { fontSize: fontBase(), color: ACCENT, cursor: 'pointer', padding: '4px' });
  btn.textContent = '✎';
  btn.addEventListener('click', onClick);
  return btn;
}
